import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	computed,
	effect,
	input,
	output,
	signal
} from '@angular/core';
import { IAngularErrorData, angularErrorHelper } from 'lib/helpers/angular-error-helper.class.js';
import { consoleHelper } from 'lib/helpers/console-helper.class.js';
import { ButtonType } from 'lib/ui/buttons/index.js';
import { Observable, catchError, finalize, map, of } from 'rxjs';
import { HttpServiceError } from 'src/app/http/http.service.js';
import { CoreComponent } from '../core/core.component.js';

export type FormSaveHandler = Observable<void> | undefined;
export type FormLogHandler = ((errorData: IAngularErrorData) => Observable<void>) | undefined;

@Component({
	selector: 'ui-form-save-button',
	templateUrl: './form-save-button.component.html',
	styleUrls: ['./form-fields.shared.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class FormSaveButtonComponent extends CoreComponent {
	@HostListener('document:keydown.control.s', ['$event'])
	handleKeyboardCtrlSaveEvent(event: KeyboardEvent) {
		if (this.enableSaveShortcut()) {
			event.preventDefault();
			this.handleSave();
		}
	}

	@HostListener('document:keydown.enter', ['$event'])
	handleKeyboardEnterEvent(event: KeyboardEvent) {
		if (this.enableEnterSaveShortcut()) {
			event.preventDefault();
			this.handleSave();
		}
	}

	@HostListener('document:keydown.control.enter', ['$event'])
	handleKeyboardCtrlEnterEvent(event: KeyboardEvent) {
		if (this.enableControlEnterSaveShortcut()) {
			event.preventDefault();
			this.handleSave();
		}
	}

	public readonly saveHandler = input.required<FormSaveHandler>();
	public readonly logHandler = input.required<FormLogHandler>();
	public readonly title = input<string>();
	public readonly removeDivider = input<boolean>(true);
	public readonly useFullSize = input<boolean>(false);
	public readonly isInDialog = input<boolean>(false);
	public readonly enableSaveShortcut = input<boolean>(true);
	public readonly enableEnterSaveShortcut = input<boolean>(false);
	public readonly enableControlEnterSaveShortcut = input<boolean>(false);
	public readonly buttonType = input<ButtonType>('primary');
	public readonly disabledButtonType = input<ButtonType>('disabled');

	public readonly isLoading = output<boolean>();

	private readonly isSaving = signal<boolean>(false);
	public readonly errorMessage = signal<string | undefined>(undefined);

	public readonly isDisabled = computed<boolean>(() => {
		return this.saveHandler() ? false : true;
	});

	constructor() {
		super();

		effect(() => {
			this.isLoading.emit(this.isSaving());
		});
	}

	handleSave(): void {
		const saveHandler = this.saveHandler();
		const isSaving = this.isSaving();

		if (!saveHandler) {
			return;
		}

		if (isSaving) {
			// save already in process
			return;
		}

		this.errorMessage.set(undefined);
		this.isSaving.set(true);

		super.subscribeToObservable(
			saveHandler.pipe(
				map(() => {}),
				catchError((error) => {
					consoleHelper.logToConsole({
						type: 'Error',
						message: `Form encountered an error`,
						data: error
					});

					const errorData = angularErrorHelper.extractErrorData(error);
					this.logFormError(errorData);
					this.errorMessage.set(this.getFriendlyErrorMessage(error));

					return of(undefined);
				}),
				finalize(() => {
					this.isSaving.set(false);
				})
			)
		);
	}

	private getFriendlyErrorMessage(error: any): string {
		if (error instanceof HttpServiceError) {
			return error.originalMessage;
		}

		return angularErrorHelper.extractErrorData(error).message;
	}

	private logFormError(data: IAngularErrorData): void {
		const logHandler = this.logHandler();

		if (!logHandler) {
			throw Error(`Invalid logHandler`);
		}
		super.subscribeToObservable(logHandler(data));
	}
}
